import { isNestedObjectArray, isObject } from '@/helper/typeGuards';

export type UnionFunctionType = (a: unknown, b: unknown) => boolean;

export function isEmpty(object: unknown): boolean {
  const valueString = typeof object === 'string' && object === '';
  const valueArray = Array.isArray(object) && object.length === 0;
  const valueInstance =
    (object instanceof Set || object instanceof Map) && object.size === 0;

  if (
    valueString ||
    valueArray ||
    object === undefined ||
    object === null ||
    valueInstance
  )
    return true;

  return (
    Object.keys(object).length === 0 &&
    Object.getPrototypeOf(object) === Object.prototype
  );
}

export function deepClone<T>(payload: T): T {
  if (payload === null || typeof payload !== 'object') {
    return payload;
  }

  if (payload instanceof Date) {
    return new Date(payload.getTime()) as unknown as T;
  }

  if (Array.isArray(payload)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return payload.map((item) => deepClone(item)) as unknown as T;
  }

  const clone = {} as { [K in keyof T]: T[K] };
  (Object.keys(payload) as (keyof T)[]).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      clone[key] = deepClone(payload[key]);
    }
  });

  return clone;
}

export function deepMerge<
  T extends Record<string, unknown>,
  S extends Record<string, unknown>,
>(target: T, source: S): T & S {
  const output = { ...target } as T & S;

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (isObject(sourceValue) && isObject(targetValue)) {
        output[key as keyof (T & S)] = deepMerge(
          targetValue,
          sourceValue,
        ) as unknown as (T & S)[keyof (T & S)];
      } else {
        output[key as keyof (T & S)] = sourceValue as unknown as (T &
          S)[keyof (T & S)];
      }
    }
  }

  return output;
}

export function toArray(input: string): string[];
export function toArray<T>(input: Record<string, T>): T[];
export function toArray<T>(input: string | Record<string, T>): string[] | T[] {
  if (typeof input === 'string') {
    return input.split('.').filter(Boolean);
  }

  if (isObject(input)) {
    return Object.values(input);
  }

  return input;
}

export function getUniq<T>(array: T[]) {
  return [...new Set(array)];
}

export function getUniqueKey() {
  const byteLength = 16; // 16 bytes for a 128-bit key
  const randomBytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(randomBytes);
  return [...randomBytes]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export function groupBy<T>(array: T[], key: keyof T) {
  return array.reduce((grouped: Record<string, T[]>, entry) => {
    if (entry[key] !== undefined) {
      const index = String(entry[key]);
      grouped[index] = grouped[index] ?? [];
      grouped[index].push(entry);
    }

    return grouped;
  }, {});
}

export function isDeepEqual(object1: unknown, object2: unknown): boolean {
  // If both objects are exactly the same reference, they are equal
  if (object1 === object2) {
    return true;
  }

  // If either object is null or undefined, they are not equal
  if (object1 == null || object2 == null) {
    return false;
  }

  // If the types of the two objects are different, they are not equal
  if (typeof object1 !== typeof object2) {
    return false;
  }

  // If the objects are not of type 'object', directly compare values
  if (typeof object1 !== 'object') {
    return object1 === object2;
  }

  // If the objects are arrays, compare their elements recursively
  if (Array.isArray(object1) && Array.isArray(object2)) {
    if (object1.length !== object2.length) {
      return false;
    }
    for (const [index, element] of object1.entries()) {
      if (!isDeepEqual(element, object2[index])) {
        return false;
      }
    }
    return true;
  }

  // Compare objects recursively by comparing their keys and values
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (
      !keys2.includes(key) ||
      !isDeepEqual(
        (object1 as Record<string, unknown>)[key],
        (object2 as Record<string, unknown>)[key],
      )
    ) {
      return false;
    }
  }
  return true;
}

type OrderMethod = 'asc' | 'desc';
export function sortBy<T>(key: keyof T, orderMethod: OrderMethod = 'asc') {
  const multiplier = orderMethod === 'desc' ? -1 : 1;

  return (a: T, b: T) => {
    if (typeof a[key] === 'string' && typeof b[key] === 'string') {
      return (
        String(a[key])
          .toLowerCase()
          .localeCompare(String(b[key]).toLowerCase()) * multiplier
      );
    }
    if (a[key] > b[key]) {
      return 1 * multiplier;
    }
    if (b[key] > a[key]) {
      return -1 * multiplier;
    }
    return 0;
  };
}

export function orderBy<T>(
  data: Record<string, T>,
  key: keyof T,
  orderMethod?: OrderMethod,
): Record<string, T>;

export function orderBy<T>(
  data: Record<string, T>[],
  key: keyof T,
  orderMethod?: OrderMethod,
): Record<string, T>[];

export function orderBy<T>(
  data: T[],
  key: keyof T,
  orderMethod?: OrderMethod,
): T[];

export function orderBy<T>(
  data: Record<string, T> | Record<string, T>[] | T[],
  key: keyof T,
  orderMethod: OrderMethod = 'asc',
): Record<string, T> | Record<string, T>[] | T[] {
  if (Array.isArray(data)) {
    if (isNestedObjectArray(data)) {
      return data.map((entry) => orderBy(entry, key, orderMethod));
    }

    return data.sort((a, b) => sortBy(key, orderMethod)(a, b));
  }

  return Object.fromEntries(
    Object.entries(data).sort(([, a], [, b]) => sortBy(key, orderMethod)(a, b)),
  );
}

type ExtendedObject<T, V> = {
  [Y in keyof T]: T[Y] & V;
};

type ExtendLayers<T, U> = {
  [Y in keyof T]: T[Y] extends object ? ExtendLayers<T[Y], U> : U;
};

export function deepSet<T extends object, V>(
  source: T,
  path: string,
  setValue: V,
) {
  function genericTypeGuard(check: unknown): check is T {
    return check !== null && typeof check === 'object';
  }
  const pathKeys = toArray(path) as unknown as (keyof typeof source)[];
  const [current, ...rest] = pathKeys;
  if (current !== undefined) {
    if (source[current] === undefined)
      source[current] = {} as ExtendedObject<T, V>[keyof T];

    const nestedObject = source[current];
    if (rest.length > 0) {
      if (genericTypeGuard(nestedObject))
        deepSet(nestedObject, rest.join('.'), setValue);
    } else {
      source[current] = setValue as ExtendedObject<T, V>[keyof T];
    }
  }

  return source as ExtendLayers<T, V>;
}

type GetIndexedField<T, K> = K extends keyof T
  ? T[K]
  : K extends `${number}`
    ? 'length' extends keyof T
      ? number extends T['length']
        ? number extends keyof T
          ? T[number]
          : undefined
        : undefined
      : undefined
    : undefined;

type FieldWithPossiblyUndefined<T, Key> =
  | GetFieldType<Exclude<T, undefined>, Key>
  | Extract<T, undefined>;

type IndexedFieldWithPossiblyUndefined<T, Key> =
  | GetIndexedField<Exclude<T, undefined>, Key>
  | Extract<T, undefined>;

type GetFieldType<T, P> = P extends `${infer Left}.${infer Right}`
  ? Left extends keyof Exclude<T, undefined>
    ?
        | FieldWithPossiblyUndefined<Exclude<T, undefined>[Left], Right>
        | Extract<T, undefined>
    : Left extends `${infer FieldKey}[${infer IndexKey}]`
      ? FieldKey extends keyof T
        ? FieldWithPossiblyUndefined<
            IndexedFieldWithPossiblyUndefined<T[FieldKey], IndexKey>,
            Right
          >
        : undefined
      : undefined
  : P extends keyof T
    ? T[P]
    : P extends `${infer FieldKey}[${infer IndexKey}]`
      ? FieldKey extends keyof T
        ? IndexedFieldWithPossiblyUndefined<T[FieldKey], IndexKey>
        : undefined
      : IndexedFieldWithPossiblyUndefined<T, P>;

export function deepGet<
  TObject extends object,
  TPath extends string,
  TDefault = GetFieldType<TObject, TPath>,
>(
  object: TObject,
  path: TPath,
  defaultValue?: TDefault,
): Exclude<GetFieldType<TObject, TPath>, null | undefined> | TDefault;

export function deepGet<
  TObject extends object,
  TPath extends string,
  TDefault = GetFieldType<TObject, TPath>,
>(
  object: TObject,
  path: TPath,
  defaultValue: TDefault,
): Exclude<GetFieldType<TObject, TPath>, null | undefined> | TDefault {
  const keys = path.split(/\.|\[(\d+)]/).filter(Boolean) as (keyof TObject)[];

  let objectGetter = object;
  for (const key of keys) {
    if (!(key in objectGetter)) return defaultValue;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    objectGetter = objectGetter[key] as any;
  }

  // @ts-expect-error: not possible to solve while being typesafe
  return objectGetter ?? defaultValue;
}

export function union<T>(a: T[], b: T[], predicate: UnionFunctionType) {
  const c = [...a];
  b.forEach((bItem) =>
    c.some((cItem) => predicate(bItem, cItem)) ? null : c.push(bItem),
  );
  return c;
}

export const forEachAsync = async <T>(
  items: T[] | Record<string, T>,
  callback: (item: T, index: number | string) => Promise<void>,
): Promise<void> => {
  const entries = Array.isArray(items)
    ? items.entries()
    : Object.entries(items);

  await [...entries].reduce(async (previousPromise, [index, item]) => {
    await previousPromise;
    return callback(item, index);
  }, Promise.resolve());
};
